import React, { useCallback, useEffect, useState } from 'react';
import { Box, Container, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Button, Tabs, Tab, TextField, InputAdornment, Switch, FormControlLabel, MenuItem, RadioGroup, Radio, Alert, useTheme, useMediaQuery, Pagination } from '@mui/material';
import { RefreshCcwIcon, SearchIcon, FilterIcon, WalletIcon, ArrowUpRightIcon, ArrowDownRightIcon, ClockIcon, CheckCircle2Icon, GlobeIcon, SendIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import KPICard from '../components/KPICard';
interface PartnerWalletProps {
  onLogout: () => void;
}
// --- Mock Data ---
const currencyBalances = [{
  currency: 'GHS',
  symbol: '₵',
  available: '1,245,890.50',
  reserved: '45,230.00',
  pendingIn: '12,000.00',
  pendingOut: '5,500.00',
  updated: 'Just now'
}, {
  currency: 'NGN',
  symbol: '₦',
  available: '8,567,234.75',
  reserved: '234,500.00',
  pendingIn: '150,000.00',
  pendingOut: '45,000.00',
  updated: '1 min ago'
}, {
  currency: 'USD',
  symbol: '$',
  available: '456,789.25',
  reserved: '12,340.00',
  pendingIn: '5,000.00',
  pendingOut: '1,200.00',
  updated: '2 mins ago'
}, {
  currency: 'EUR',
  symbol: '€',
  available: '234,567.80',
  reserved: '8,900.00',
  pendingIn: '2,500.00',
  pendingOut: '800.00',
  updated: '5 mins ago'
}, {
  currency: 'XOF',
  symbol: 'CFA',
  available: '12,345,678.00',
  reserved: '567,890.00',
  pendingIn: '450,000.00',
  pendingOut: '120,000.00',
  updated: '10 mins ago'
}];
const regionBalances = [{
  region: 'West Africa',
  balance: 'USD 1.8M equivalent',
  trend: '+5.2%'
}, {
  region: 'East Africa',
  balance: 'USD 450K equivalent',
  trend: '+1.8%'
}, {
  region: 'Southern Africa',
  balance: 'USD 280K equivalent',
  trend: '-0.5%'
}];
const generateTransactions = () => {
  const types = ['Credit', 'Debit'];
  const statuses = ['Completed', 'Pending', 'Failed', 'Processing'];
  const currencies = ['USD', 'GHS', 'NGN', 'EUR'];
  const txns = [];
  for (let i = 1; i <= 15; i++) {
    const type = types[Math.floor(Math.random() * types.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const currency = currencies[Math.floor(Math.random() * currencies.length)];
    const amount = (Math.random() * 10000).toFixed(2);
    txns.push({
      id: `TRX-${100000 + i}`,
      date: `2026-04-${24 - Math.floor(i / 3)} ${10 + i % 12}:${15 + i} AM`,
      ref: `REF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
      type,
      description: type === 'Credit' ? 'Partner Deposit' : 'Payout Settlement',
      amount,
      currency,
      status,
      balanceAfter: (500000 + (type === 'Credit' ? +amount : -amount)).toFixed(2)
    });
  }
  return txns;
};
const mockTransactions = generateTransactions();
const recentTransfers = [{
  id: 1,
  to: 'W-9823 (Internal)',
  amount: '5,000.00 USD',
  status: 'Completed',
  time: '2 hours ago'
}, {
  id: 2,
  to: 'GT Bank (External)',
  amount: '2,500,000 NGN',
  status: 'Processing',
  time: '5 hours ago'
}, {
  id: 3,
  to: 'W-4451 (Partner)',
  amount: '10,000.00 GHS',
  status: 'Completed',
  time: '1 day ago'
}];
export const PartnerWallet: React.FC<PartnerWalletProps> = ({
  onLogout
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [transferForm, setTransferForm] = useState({
    fromWallet: 'USD',
    toType: 'another_wallet',
    recipient: '',
    amount: '',
    currency: 'USD',
    reference: ''
  });
  const [transferSuccess, setTransferSuccess] = useState(false);
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const refreshData = useCallback(() => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }, 1000);
  }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshData]);
  const handleTransferSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTransferSuccess(true);
    setTimeout(() => {
      setTransferSuccess(false);
      setTransferForm({
        ...transferForm,
        recipient: '',
        amount: '',
        reference: ''
      });
    }, 5000);
  };
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'Completed':
        return <Chip label="Completed" size="small" sx={{
          backgroundColor: '#ECFDF5',
          color: '#0B7923',
          border: '1px solid #A7F3D0',
          fontWeight: 600
        }} data-id="element-1939" />;
      case 'Pending':
        return <Chip label="Pending" size="small" sx={{
          backgroundColor: '#FFFBEB',
          color: '#B38600',
          border: '1px solid #FDE68A',
          fontWeight: 600
        }} data-id="element-1940" />;
      case 'Failed':
        return <Chip label="Failed" size="small" sx={{
          backgroundColor: '#FEF2F2',
          color: '#AE2A19',
          border: '1px solid #FECACA',
          fontWeight: 600
        }} data-id="element-1941" />;
      case 'Processing':
        return <Chip label="Processing" size="small" sx={{
          backgroundColor: '#EFF6FF',
          color: '#0052CC',
          border: '1px solid #BFDBFE',
          fontWeight: 600
        }} data-id="element-1942" />;
      default:
        return <Chip label={status} size="small" data-id="element-1943" />;
    }
  };
  const filteredTransactions = mockTransactions.filter(t => {
    if (tabValue === 1 && t.type !== 'Credit') return false;
    if (tabValue === 2 && t.type !== 'Debit') return false;
    if (tabValue === 3 && t.status !== 'Pending') return false;
    if (searchQuery && !t.ref.toLowerCase().includes(searchQuery.toLowerCase()) && !t.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-1944">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-1945" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="partner-wallet" data-id="element-1946" />

      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      pb: 10,
      width: {
        sm: 'calc(100% - 240px)'
      },
      ml: 0,
      mt: 8
    }} data-id="element-1947">
        <Container maxWidth={false} sx={{
        maxWidth: 1600
      }} data-id="element-1948">
          {/* Header & Real-Time Status Bar */}
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }} data-id="element-1949">
            <Box data-id="element-1950">
              <Typography variant="h4" sx={{
              fontWeight: 700,
              color: '#1F2937',
              fontSize: '2rem',
              mb: 0.5
            }} data-id="element-1951">
                Partner Wallet Management
              </Typography>
              <Typography variant="body2" sx={{
              color: '#6B7280',
              fontSize: '0.95rem'
            }} data-id="element-1952">
                Manage balances, transfers, and transaction history
              </Typography>
            </Box>

            <Paper sx={{
            p: 1.5,
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            backgroundColor: 'white',
            boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
          }} data-id="element-1953">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }} data-id="element-1954">
                <Box sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: isRefreshing ? '#B38600' : '#0B7923',
                animation: isRefreshing ? 'pulse 1.5s infinite' : 'none'
              }} data-id="element-1955" />
                <Typography variant="body2" sx={{
                fontWeight: 600,
                color: '#1F2937'
              }} data-id="element-1956">
                  {isRefreshing ? 'Syncing...' : 'Connected'}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{
              color: '#6B7280'
            }} data-id="element-1957">
                Last synced:{' '}
                {lastUpdated.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
              </Typography>
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderLeft: '1px solid #E5E7EB',
              pl: 2
            }} data-id="element-1958">
                <FormControlLabel control={<Switch size="small" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} color="primary" data-id="element-1960" />} label={<Typography variant="caption" sx={{
                fontWeight: 600,
                color: '#4B5563'
              }} data-id="element-1961">
                      Auto-refresh
                    </Typography>} sx={{
                m: 0
              }} data-id="element-1959" />
                <Button variant="outlined" size="small" onClick={refreshData} disabled={isRefreshing} startIcon={<RefreshCcwIcon size={14} data-id="element-1963" />} sx={{
                borderRadius: '6px',
                textTransform: 'none',
                borderColor: '#E5E7EB',
                color: '#4B5563',
                '&:hover': {
                  backgroundColor: '#F3F5F7',
                  borderColor: '#D1D5DB'
                }
              }} data-id="element-1962">
                  Refresh
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Row 1: KPI Cards */}
          <Grid container spacing={3} sx={{
          mb: 4
        }} data-id="element-1964">
            <Grid item xs={12} sm={6} md={3} data-id="element-1965">
              <KPICard title="TOTAL WALLET BALANCE" value="USD 2.45M" icon={<WalletIcon size={20} data-id="element-1967" />} color="#0052CC" subDetails={[{
              label: 'Equivalent Value',
              value: 'All Currencies'
            }]} data-id="element-1966" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1968">
              <KPICard title="AVAILABLE BALANCE" value="USD 2.12M" icon={<CheckCircle2Icon size={20} data-id="element-1970" />} color="#0B7923" subDetails={[{
              label: 'Ready for Payouts',
              value: '86.5%'
            }]} data-id="element-1969" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1971">
              <KPICard title="RESERVED / HELD" value="USD 185K" icon={<ClockIcon size={20} data-id="element-1973" />} color="#B38600" subDetails={[{
              label: 'Pending Compliance',
              value: '7.5%'
            }]} data-id="element-1972" />
            </Grid>
            <Grid item xs={12} sm={6} md={3} data-id="element-1974">
              <KPICard title="PENDING SETTLEMENTS" value="USD 145K" icon={<ArrowDownRightIcon size={20} data-id="element-1976" />} color="#6B7280" subDetails={[{
              label: 'Inbound Transfers',
              value: '6.0%'
            }]} data-id="element-1975" />
            </Grid>
          </Grid>

          {/* Row 2: Balance Overview */}
          <Typography variant="overline" sx={{
          fontWeight: 600,
          color: '#6B7280',
          mb: 1.5,
          display: 'block',
          letterSpacing: '0.05em'
        }} data-id="element-1977">
            BALANCE OVERVIEW
          </Typography>
          <Paper sx={{
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
          backgroundColor: 'white',
          mb: 4,
          overflow: 'hidden'
        }} data-id="element-1978">
            <Box sx={{
            p: 2.5,
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }} data-id="element-1979">
              <Typography variant="subtitle1" sx={{
              fontWeight: 700,
              color: '#1F2937'
            }} data-id="element-1980">
                Multi-Currency Wallets
              </Typography>
              <Chip label="Live" size="small" sx={{
              backgroundColor: '#ECFDF5',
              color: '#0B7923',
              fontWeight: 600,
              border: '1px solid #A7F3D0'
            }} data-id="element-1981" />
            </Box>
            <TableContainer data-id="element-1982">
              <Table data-id="element-1983">
                <TableHead data-id="element-1984">
                  <TableRow sx={{
                  backgroundColor: '#F8F9FA'
                }} data-id="element-1985">
                    <TableCell sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1986">
                      Currency
                    </TableCell>
                    <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1987">
                      Available Balance
                    </TableCell>
                    <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1988">
                      Reserved
                    </TableCell>
                    <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1989">
                      Pending In
                    </TableCell>
                    <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1990">
                      Pending Out
                    </TableCell>
                    <TableCell align="right" sx={{
                    fontWeight: 600,
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase'
                  }} data-id="element-1991">
                      Last Updated
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-1992">
                  {currencyBalances.map((row, idx) => <TableRow key={idx} hover data-id="element-1993">
                      <TableCell data-id="element-1994">
                        <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }} data-id="element-1995">
                          <Box sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '6px',
                        backgroundColor: '#F0F3F7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        color: '#0052CC'
                      }} data-id="element-1996">
                            {row.currency}
                          </Box>
                          <Typography variant="body2" sx={{
                        fontWeight: 600,
                        color: '#1F2937'
                      }} data-id="element-1997">
                            {row.symbol}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{
                    fontWeight: 700,
                    color: '#1F2937',
                    fontSize: '0.95rem'
                  }} data-id="element-1998">
                        {row.symbol} {row.available}
                      </TableCell>
                      <TableCell align="right" sx={{
                    color: '#6B7280'
                  }} data-id="element-1999">
                        {row.symbol} {row.reserved}
                      </TableCell>
                      <TableCell align="right" sx={{
                    color: '#0B7923'
                  }} data-id="element-2000">
                        +{row.symbol} {row.pendingIn}
                      </TableCell>
                      <TableCell align="right" sx={{
                    color: '#AE2A19'
                  }} data-id="element-2001">
                        -{row.symbol} {row.pendingOut}
                      </TableCell>
                      <TableCell align="right" sx={{
                    color: '#6B7280',
                    fontSize: '0.8rem'
                  }} data-id="element-2002">
                        {row.updated}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Region Breakdown Sub-section */}
            <Box sx={{
            p: 2.5,
            backgroundColor: '#F8F9FA',
            borderTop: '1px solid #E5E7EB'
          }} data-id="element-2003">
              <Typography variant="caption" sx={{
              fontWeight: 600,
              color: '#6B7280',
              textTransform: 'uppercase',
              mb: 2,
              display: 'block'
            }} data-id="element-2004">
                Regional Exposure
              </Typography>
              <Grid container spacing={2} data-id="element-2005">
                {regionBalances.map((region, idx) => <Grid item xs={12} sm={4} key={idx} data-id="element-2006">
                    <Box sx={{
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }} data-id="element-2007">
                      <Box sx={{
                    p: 1,
                    borderRadius: '6px',
                    backgroundColor: '#F0F3F7',
                    color: '#0052CC'
                  }} data-id="element-2008">
                        <GlobeIcon size={20} data-id="element-2009" />
                      </Box>
                      <Box data-id="element-2010">
                        <Typography variant="body2" sx={{
                      fontWeight: 600,
                      color: '#1F2937'
                    }} data-id="element-2011">
                          {region.region}
                        </Typography>
                        <Typography variant="caption" sx={{
                      color: '#6B7280',
                      display: 'block'
                    }} data-id="element-2012">
                          {region.balance}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>)}
              </Grid>
            </Box>
          </Paper>

          {/* Row 3: Transaction History & Fund Transfer */}
          <Grid container spacing={3} data-id="element-2013">
            {/* Left Column: Transaction History */}
            <Grid item xs={12} md={8} data-id="element-2014">
              <Typography variant="overline" sx={{
              fontWeight: 600,
              color: '#6B7280',
              mb: 1.5,
              display: 'block',
              letterSpacing: '0.05em'
            }} data-id="element-2015">
                TRANSACTION HISTORY
              </Typography>
              <Paper sx={{
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              overflow: 'hidden'
            }} data-id="element-2016">
                <Box sx={{
                borderBottom: '1px solid #E5E7EB'
              }} data-id="element-2017">
                  <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{
                  px: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 100
                  },
                  '& .Mui-selected': {
                    color: '#0052CC'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#0052CC'
                  }
                }} data-id="element-2018">
                    <Tab label="All Transactions" data-id="element-2019" />
                    <Tab label="Credits" data-id="element-2020" />
                    <Tab label="Debits" data-id="element-2021" />
                    <Tab label="Pending" data-id="element-2022" />
                  </Tabs>
                </Box>

                <Box sx={{
                p: 2,
                display: 'flex',
                gap: 2,
                borderBottom: '1px solid #E5E7EB',
                backgroundColor: '#F8F9FA'
              }} data-id="element-2023">
                  <TextField placeholder="Search reference or description..." size="small" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} InputProps={{
                  startAdornment: <InputAdornment position="start" data-id="element-2025">
                          <SearchIcon size={16} color="#6B7280" data-id="element-2026" />
                        </InputAdornment>
                }} sx={{
                  width: 300,
                  backgroundColor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px'
                  }
                }} data-id="element-2024" />
                  <Button variant="outlined" startIcon={<FilterIcon size={16} data-id="element-2028" />} sx={{
                  borderRadius: '6px',
                  textTransform: 'none',
                  borderColor: '#E5E7EB',
                  color: '#4B5563',
                  backgroundColor: 'white'
                }} data-id="element-2027">
                    Filters
                  </Button>
                </Box>

                <TableContainer sx={{
                maxHeight: 500
              }} data-id="element-2029">
                  <Table stickyHeader size="small" data-id="element-2030">
                    <TableHead data-id="element-2031">
                      <TableRow data-id="element-2032">
                        <TableCell sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2033">
                          Date/Time
                        </TableCell>
                        <TableCell sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2034">
                          Reference
                        </TableCell>
                        <TableCell sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2035">
                          Description
                        </TableCell>
                        <TableCell align="right" sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2036">
                          Amount
                        </TableCell>
                        <TableCell sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2037">
                          Status
                        </TableCell>
                        <TableCell align="right" sx={{
                        fontWeight: 600,
                        color: '#6B7280',
                        backgroundColor: 'white'
                      }} data-id="element-2038">
                          Balance After
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody data-id="element-2039">
                      {filteredTransactions.map(txn => <TableRow key={txn.id} hover data-id="element-2040">
                          <TableCell sx={{
                        color: '#6B7280',
                        fontSize: '0.85rem'
                      }} data-id="element-2041">
                            {txn.date}
                          </TableCell>
                          <TableCell sx={{
                        fontWeight: 500,
                        color: '#1F2937',
                        fontSize: '0.85rem'
                      }} data-id="element-2042">
                            {txn.ref}
                          </TableCell>
                          <TableCell data-id="element-2043">
                            <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }} data-id="element-2044">
                              {txn.type === 'Credit' ? <ArrowDownRightIcon size={14} color="#0B7923" data-id="element-2045" /> : <ArrowUpRightIcon size={14} color="#AE2A19" data-id="element-2046" />}
                              <Typography variant="body2" sx={{
                            color: '#1F2937',
                            fontSize: '0.85rem'
                          }} data-id="element-2047">
                                {txn.description}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{
                        fontWeight: 600,
                        color: txn.type === 'Credit' ? '#0B7923' : '#1F2937',
                        fontSize: '0.85rem'
                      }} data-id="element-2048">
                            {txn.type === 'Credit' ? '+' : '-'}
                            {txn.amount} {txn.currency}
                          </TableCell>
                          <TableCell data-id="element-2049">{getStatusChip(txn.status)}</TableCell>
                          <TableCell align="right" sx={{
                        color: '#6B7280',
                        fontSize: '0.85rem'
                      }} data-id="element-2050">
                            {txn.balanceAfter} {txn.currency}
                          </TableCell>
                        </TableRow>)}
                      {filteredTransactions.length === 0 && <TableRow data-id="element-2051">
                          <TableCell colSpan={6} align="center" sx={{
                        py: 4,
                        color: '#6B7280'
                      }} data-id="element-2052">
                            No transactions found.
                          </TableCell>
                        </TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{
                p: 2,
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-2053">
                  <Typography variant="body2" sx={{
                  color: '#6B7280'
                }} data-id="element-2054">
                    Showing 1-{Math.min(15, filteredTransactions.length)} of 234
                  </Typography>
                  <Pagination count={16} shape="rounded" color="primary" size="small" data-id="element-2055" />
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Fund Transfer */}
            <Grid item xs={12} md={4} data-id="element-2056">
              <Typography variant="overline" sx={{
              fontWeight: 600,
              color: '#6B7280',
              mb: 1.5,
              display: 'block',
              letterSpacing: '0.05em'
            }} data-id="element-2057">
                FUND TRANSFER
              </Typography>
              <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
              backgroundColor: 'white',
              mb: 3
            }} data-id="element-2058">
                {transferSuccess && <Alert severity="success" sx={{
                mb: 3,
                borderRadius: '6px'
              }} data-id="element-2059">
                    Transfer initiated successfully.
                  </Alert>}
                <form onSubmit={handleTransferSubmit} data-id="element-2060">
                  <Box sx={{
                  mb: 2.5
                }} data-id="element-2061">
                    <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }} data-id="element-2062">
                      From Wallet
                    </Typography>
                    <TextField select fullWidth size="small" value={transferForm.fromWallet} onChange={e => setTransferForm({
                    ...transferForm,
                    fromWallet: e.target.value
                  })} sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px'
                    }
                  }} data-id="element-2063">
                      {currencyBalances.map(cb => <MenuItem key={cb.currency} value={cb.currency} data-id="element-2064">
                          {cb.currency} - Available: {cb.symbol}
                          {cb.available}
                        </MenuItem>)}
                    </TextField>
                  </Box>

                  <Box sx={{
                  mb: 2.5
                }} data-id="element-2065">
                    <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }} data-id="element-2066">
                      Transfer To
                    </Typography>
                    <RadioGroup row value={transferForm.toType} onChange={e => setTransferForm({
                    ...transferForm,
                    toType: e.target.value
                  })} data-id="element-2067">
                      <FormControlLabel value="another_wallet" control={<Radio size="small" sx={{
                      color: '#0052CC',
                      '&.Mui-checked': {
                        color: '#0052CC'
                      }
                    }} data-id="element-2069" />} label={<Typography variant="body2" data-id="element-2070">
                            Another Wallet
                          </Typography>} data-id="element-2068" />
                      <FormControlLabel value="partner" control={<Radio size="small" sx={{
                      color: '#0052CC',
                      '&.Mui-checked': {
                        color: '#0052CC'
                      }
                    }} data-id="element-2072" />} label={<Typography variant="body2" data-id="element-2073">Partner</Typography>} data-id="element-2071" />
                      <FormControlLabel value="external" control={<Radio size="small" sx={{
                      color: '#0052CC',
                      '&.Mui-checked': {
                        color: '#0052CC'
                      }
                    }} data-id="element-2075" />} label={<Typography variant="body2" data-id="element-2076">External</Typography>} data-id="element-2074" />
                    </RadioGroup>
                  </Box>

                  <Box sx={{
                  mb: 2.5
                }} data-id="element-2077">
                    <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }} data-id="element-2078">
                      Recipient / Account
                    </Typography>
                    <TextField fullWidth size="small" placeholder="Enter ID or Account Number" value={transferForm.recipient} onChange={e => setTransferForm({
                    ...transferForm,
                    recipient: e.target.value
                  })} required sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px'
                    }
                  }} data-id="element-2079" />
                  </Box>

                  <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2.5
                }} data-id="element-2080">
                    <Box sx={{
                    flex: 2
                  }} data-id="element-2081">
                      <Typography variant="body2" sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 1
                    }} data-id="element-2082">
                        Amount
                      </Typography>
                      <TextField fullWidth size="small" type="number" placeholder="0.00" value={transferForm.amount} onChange={e => setTransferForm({
                      ...transferForm,
                      amount: e.target.value
                    })} required sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px'
                      }
                    }} data-id="element-2083" />
                    </Box>
                    <Box sx={{
                    flex: 1
                  }} data-id="element-2084">
                      <Typography variant="body2" sx={{
                      fontWeight: 600,
                      color: '#374151',
                      mb: 1
                    }} data-id="element-2085">
                        Currency
                      </Typography>
                      <TextField select fullWidth size="small" value={transferForm.currency} onChange={e => setTransferForm({
                      ...transferForm,
                      currency: e.target.value
                    })} sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px'
                      }
                    }} data-id="element-2086">
                        <MenuItem value="USD" data-id="element-2087">USD</MenuItem>
                        <MenuItem value="EUR" data-id="element-2088">EUR</MenuItem>
                        <MenuItem value="GHS" data-id="element-2089">GHS</MenuItem>
                        <MenuItem value="NGN" data-id="element-2090">NGN</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  <Box sx={{
                  mb: 3
                }} data-id="element-2091">
                    <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }} data-id="element-2092">
                      Reference / Note
                    </Typography>
                    <TextField fullWidth size="small" placeholder="Optional reference" value={transferForm.reference} onChange={e => setTransferForm({
                    ...transferForm,
                    reference: e.target.value
                  })} sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px'
                    }
                  }} data-id="element-2093" />
                  </Box>

                  <Button type="submit" fullWidth variant="contained" endIcon={<SendIcon size={16} data-id="element-2095" />} sx={{
                  backgroundColor: '#0052CC',
                  py: 1.2,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#003DA8',
                    boxShadow: 'none'
                  }
                }} data-id="element-2094">
                    Initiate Transfer
                  </Button>
                </form>
              </Paper>

              <Typography variant="overline" sx={{
              fontWeight: 600,
              color: '#6B7280',
              mb: 1.5,
              display: 'block',
              letterSpacing: '0.05em'
            }} data-id="element-2096">
                RECENT TRANSFERS
              </Typography>
              <Paper sx={{
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              backgroundColor: 'white',
              overflow: 'hidden'
            }} data-id="element-2097">
                {recentTransfers.map((rt, idx) => <Box key={rt.id} sx={{
                p: 2,
                borderBottom: idx < recentTransfers.length - 1 ? '1px solid #E5E7EB' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }} data-id="element-2098">
                    <Box data-id="element-2099">
                      <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: '#1F2937'
                  }} data-id="element-2100">
                        To: {rt.to}
                      </Typography>
                      <Typography variant="caption" sx={{
                    color: '#6B7280'
                  }} data-id="element-2101">
                        {rt.time}
                      </Typography>
                    </Box>
                    <Box sx={{
                  textAlign: 'right'
                }} data-id="element-2102">
                      <Typography variant="body2" sx={{
                    fontWeight: 700,
                    color: '#1F2937'
                  }} data-id="element-2103">
                        {rt.amount}
                      </Typography>
                      <Typography variant="caption" sx={{
                    color: rt.status === 'Completed' ? '#0B7923' : '#B38600',
                    fontWeight: 600
                  }} data-id="element-2104">
                        {rt.status}
                      </Typography>
                    </Box>
                  </Box>)}
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>;
};
export default PartnerWallet;